<template>
  <template v-if="worker">
    <div class="side-panel">
      <template v-if="!simple">
        <div class="buttons">
          <EditForm
            class="is-primary"
            :worker="worker"
          />
          <button
            v-if="!worker.archived"
            class="button is-danger"
            v-on:click="archiveModal = canArchive"
            :disabled="!canArchive"
            :title="
              canArchive
                ? 'Archive worker'
                : 'You do not have the necessary permission to archive a worker'
            "
          >
            Archive worker
          </button>
        </div>
        <ArchivalModal
          v-if="worker"
          :instance="worker"
          instance-type="worker"
          v-model="archiveModal"
          :update-function="updateWorker"
        />
      </template>

      <RecommendedVersionButton
        v-if="!local"
        :worker-id="worker.id"
        :process-id="processId"
        v-on:selected-version="(version) => $emit('selected-version', version)"
      />
    </div>

    <div class="title">
      <span
        v-if="worker.archived"
        class="tag"
        >Archived</span
      >
      <WorkerTag
        :worker="worker"
        :with-link="simple"
      />
    </div>
    <div class="subtitle is-5">
      <p>
        Worker <ItemId :item-id="worker.id" />
        <span
          v-if="hasCosts(worker)"
          class="tag is-warning"
          >€</span
        >
      </p>
    </div>

    <Tabs
      :tabs="tabs"
      v-model="tab"
    >
      <template v-slot:details>
        <!-- eslint-disable vue/no-v-html -->
        <p
          v-if="worker.description"
          class="content"
          v-html="md.render(worker.description)"
        ></p>
        <!-- eslint-enable vue/no-v-html -->
        <p v-else><em>No description available</em></p>

        <template v-if="!simple">
          <hr />
          <div
            class="field"
            v-if="!simple"
          >
            <label class="label">Repository</label>
            <div class="control">
              <p
                v-if="worker.repository_url"
                class="content"
              >
                <a
                  :href="worker.repository_url"
                  target="_blank"
                >
                  {{ worker.repository_url }}
                </a>
              </p>
              <p v-else>—</p>
            </div>
          </div>
        </template>
      </template>
      <template v-slot:versions>
        <VersionList
          :worker="worker"
          :selectable="selectable"
          :process-id="processId"
          :local="local"
          v-on:selected-version="(version) => $emit('selected-version', version)"
        />
      </template>
      <template v-slot:members>
        <ListMembers
          v-if="hasFeature('enterprise') && !simple"
          content-type="worker"
          :content-id="workerId"
        />
      </template>
      <template
        v-slot:costs
        v-if="hasCosts(worker)"
      >
        <WorkerCosts :worker="worker" />
      </template>
    </Tabs>
  </template>
  <div
    v-else-if="error"
    class="notification is-warning"
  >
    {{ error }}
  </div>
  <div
    v-else
    class="notification is-info"
  >
    Loading…
  </div>
</template>

<script lang="ts">
import MarkdownIt from 'markdown-it'
import { mapActions, mapState } from 'pinia'
import { type PropType, defineComponent } from 'vue'

import ArchivalModal from '@/components/ArchivalModal.vue'
import ItemId from '@/components/ItemId.vue'
import ListMembers from '@/components/Memberships/ListMembers.vue'
import Tabs from '@/components/Tabs.vue'
import { errorParser } from '@/helpers'
import { hasCosts } from '@/helpers/worker'
import { useAuthStore, useNotificationStore, useWorkerStore } from '@/stores'
import type { UUID } from '@/types'
import type { Worker, WorkerVersion } from '@/types/worker'

import WorkerCosts from './Costs.vue'
import EditForm from './EditForm.vue'
import VersionList from './Versions/List.vue'
import RecommendedVersionButton from './Versions/RecommendedButton.vue'
import WorkerTag from './WorkerRuns/WorkerTag.vue'

export default defineComponent({
  emits: {
    'selected-version': (value: WorkerVersion) =>
      typeof value === 'object' && value !== null && typeof value.id === 'string',
  },
  props: {
    workerId: {
      type: String as PropType<UUID>,
      required: true,
    },
    /**
     * Whether or not buttons are shown to allow selecting a worker version.
     * This will trigger `selected-version` events.
     *
     * Without `processId` or `local` set, any worker version can be selected.
     */
    selectable: {
      type: Boolean,
      default: false,
    },
    /**
     * If `selectable` is set, selecting a version will both cause an event
     * and create a WorkerRun on the specified process.
     *
     * Only available versions will be selectable.
     */
    processId: {
      type: String as PropType<UUID | null>,
      default: null,
    },
    /**
     * If true, only worker versions that do not have a revision attached
     * can be selected (for creating local worker runs).
     *
     * This has no effect if `selectable` is not set.
     */
    local: {
      type: Boolean,
      default: false,
    },
    /**
     * Show less details, useful for inserting this component into a modal
     */
    simple: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    ArchivalModal,
    EditForm,
    ItemId,
    ListMembers,
    RecommendedVersionButton,
    Tabs,
    VersionList,
    WorkerCosts,
    WorkerTag,
  },
  data: () => ({
    loading: false,
    error: null as string | null,
    archiveModal: false,
    md: new MarkdownIt({ breaks: true }),
    tab: 'details',
  }),
  computed: {
    ...mapState(useWorkerStore, ['workers']),
    ...mapState(useAuthStore, ['hasFeature', 'user', 'canArchive']),
    worker(): Worker | null {
      return this.workers[this.workerId] ?? null
    },
    tabs(): Record<string, string> {
      const tabs: Record<string, string> = {
        // In simple mode, the details tab only shows the worker's description
        details: this.simple ? 'Description' : 'Details',
        versions: 'Versions',
      }
      if (this.hasFeature('enterprise') && !this.simple) tabs.members = 'Members'
      if (this.worker && hasCosts(this.worker)) tabs.costs = 'Costs'
      return tabs
    },
  },
  methods: {
    ...mapActions(useWorkerStore, ['getWorker', 'updateWorker']),
    ...mapActions(useNotificationStore, ['notify']),
    hasCosts,
  },
  watch: {
    workerId: {
      immediate: true,
      async handler(newValue: UUID) {
        if (this.workers[newValue]) return
        this.loading = true
        try {
          await this.getWorker(this.workerId)
        } catch (err) {
          this.error = errorParser(err)
        } finally {
          this.loading = false
        }
      },
    },
  },
})
</script>

<style scoped>
.side-panel {
  float: right;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  margin-left: 0.5rem;
}

.side-panel .buttons {
  margin-left: auto;
  margin-bottom: 0.25rem;
}
</style>
